import type { FormProps } from 'antd/es/form';
import {
  useCalculPrimeConcessionnaire,
  useNouveauDossierConcessionnaireMutation,
} from 'hooks/dossiers.hooks';
import {
  ClientType,
  NouveauDossierConcessionnaireData,
  NouveauDossierConcessionnaireParams,
  YesOrNo,
} from 'types/Concessionnaire';
import { appendDossierFormDataDocuments } from 'utils/dossier';

type FormValues = Parameters<NonNullable<FormProps['onFinish']>>[0];

interface Args {
  state: Partial<NouveauDossierConcessionnaireData>;
  setState: React.Dispatch<
    React.SetStateAction<Partial<NouveauDossierConcessionnaireData>>
  >;
}

export interface UseSubmitNouveauDossierConcessionnaireReturn {
  submitStep1: (values: FormValues) => void;
  submitStep2: (values: FormValues) => Promise<boolean>;
  buildFinalSubmitVariables: (
    values: FormValues,
  ) => { particulier: boolean; formData: FormData } | undefined;
  step2Mutation: ReturnType<typeof useCalculPrimeConcessionnaire>;
  step3Mutation: ReturnType<typeof useNouveauDossierConcessionnaireMutation>;
  isPersonnePhysique: boolean;
}

export function useSubmitNouveauDossierConcessionnaire({
  state,
  setState,
}: Args): UseSubmitNouveauDossierConcessionnaireReturn {
  const step2Mutation = useCalculPrimeConcessionnaire();
  const step3Mutation = useNouveauDossierConcessionnaireMutation();

  function submitStep1(values: FormValues) {
    setState((prev) => ({ ...prev, ...values }));
  }

  async function submitStep2(values: FormValues) {
    const nextState = { ...state, ...values };
    const isPersonnePhysique =
      nextState.typeClientFinal === ClientType.PERSONNE_PHYSIQUE;
    let ficheDeLiaisonId: string | undefined;

    try {
      if (!isPersonnePhysique) {
        ficheDeLiaisonId = nextState.ficheDeLiaison?.id;

        if (!ficheDeLiaisonId) {
          console.error(
            new Error('Fiche de liaison manquante pour le calcul de la prime'),
          );
          return false;
        }
      }

      const result = await step2Mutation.mutateAsync(
        isPersonnePhysique
          ? {
              typeClientFinal: nextState.typeClientFinal!,
              categorieVehicule: values.categorieVehicule,
              scoreEnvironnementalAtteint: values.scoreEnvironnementalAtteint
                ? values.scoreEnvironnementalAtteint === YesOrNo.OUI
                : undefined,
              coutAcquisitionInferieur47000:
                values.coutAcquisitionInferieur47000
                  ? values.coutAcquisitionInferieur47000 === YesOrNo.OUI
                  : undefined,
              masseVehiculeInferieure2400: values.masseVehiculeInferieure2400
                ? values.masseVehiculeInferieure2400 === YesOrNo.OUI
                : undefined,
              dejaBeneficieBonusEcologique: values.dejaBeneficieBonusEcologique
                ? values.dejaBeneficieBonusEcologique === YesOrNo.OUI
                : undefined,
              produitEnEurope: values.produitEnEurope
                ? values.produitEnEurope === YesOrNo.OUI
                : undefined,
              vehiculeAdeme: values.vehiculeAdeme
                ? values.vehiculeAdeme === YesOrNo.OUI
                : undefined,
              datePrevisionnelleDevis:
                values.datePrevisionnelleDevis?.format('YYYY-MM-DD')!,
              codePostal: nextState.codePostal!,
              usageVehiculeParticulier: values.usageVehiculeParticulier,
              statutPrecarite: nextState.statutPrecarite!,
            }
          : {
              typeClientFinal: nextState.typeClientFinal!,
              categorieVehicule: values.categorieVehicule,
              typeAcheteur: nextState.typeAcheteur!,
              ficheDeLiaisonId: ficheDeLiaisonId!,
              usageVehicules: values.usageVehicules,
              produitEnEurope: values.produitEnEurope
                ? values.produitEnEurope === YesOrNo.OUI
                : undefined,
            },
      );

      setState((prev) => ({
        ...prev,
        ...values,
        primeParVehicule: result.primeParVehicule,
        primeParGwh: result.primeParGwh,
        volume: result.volume,
      }));

      return true;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  function buildFinalSubmitVariables(values: FormValues) {
    const payload = {
      ...state,
      ...values,
    } as NouveauDossierConcessionnaireData;

    try {
      const formData = new FormData();
      const {
        dateEditionDevis,
        dateDevis,
        dateLivraison,
        datePrevisionnelleDevis,
        scoreEnvironnementalAtteint,
        coutAcquisitionInferieur47000,
        masseVehiculeInferieure2400,
        dejaBeneficieBonusEcologique,
        produitEnEurope,
        vehiculeAdeme,
        ...rest
      } = payload;
      const data: NouveauDossierConcessionnaireParams = {
        ...rest,
        dateEditionDevis: dateEditionDevis?.format('YYYY-MM-DD'),
        dateDevis: dateDevis?.format('YYYY-MM-DD'),
        datePrevisionnelleDevis: datePrevisionnelleDevis?.format('YYYY-MM-DD'),
        dateLivraison: dateLivraison.format('YYYY-MM-DD'),
        scoreEnvironnementalAtteint: scoreEnvironnementalAtteint
          ? scoreEnvironnementalAtteint === YesOrNo.OUI
          : undefined,
        coutAcquisitionInferieur47000: coutAcquisitionInferieur47000
          ? coutAcquisitionInferieur47000 === YesOrNo.OUI
          : undefined,
        masseVehiculeInferieure2400: masseVehiculeInferieure2400
          ? masseVehiculeInferieure2400 === YesOrNo.OUI
          : undefined,
        dejaBeneficieBonusEcologique: dejaBeneficieBonusEcologique
          ? dejaBeneficieBonusEcologique === YesOrNo.OUI
          : undefined,
        produitEnEurope: produitEnEurope
          ? produitEnEurope === YesOrNo.OUI
          : undefined,
        vehiculeAdeme: vehiculeAdeme
          ? vehiculeAdeme === YesOrNo.OUI
          : undefined,
        ficheDeLiaisonId: state.ficheDeLiaison?.id,
        rai: state.ficheDeLiaison?.rai,
      };

      formData.append('data', JSON.stringify(data));

      appendDossierFormDataDocuments(payload.devis, formData, [
        'pièce just. de début des travaux',
      ]);

      if (payload.typeClientFinal === ClientType.PERSONNE_PHYSIQUE) {
        appendDossierFormDataDocuments(payload.avisImposition, formData, [
          'autre',
        ]);
      }
      appendDossierFormDataDocuments(payload?.otherFiles ?? [], formData, [
        'autre',
      ]);

      return {
        particulier: payload.typeClientFinal === ClientType.PERSONNE_PHYSIQUE,
        formData,
      };
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  return {
    submitStep1,
    submitStep2,
    buildFinalSubmitVariables,
    step2Mutation,
    step3Mutation,
    isPersonnePhysique: state.typeClientFinal === ClientType.PERSONNE_PHYSIQUE,
  };
}
